import { logger } from '../../util/logger';
import { StreemshotId } from '@streem/domain-id';
import { invariant } from '@streem/toolbox';
import { combineLatest } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { takeFirstEmitted } from '../../util/rx/take_first_emitted';
import { accumulateAndMapWallEvents, filterByProperty, findInGroup } from '../wall/utils';
export class DetectionController {
    constructor(config) {
        this.config = config;
        this.streemshots = this.config.roomWall.itemsByType.pipe(findInGroup('streemshot'), accumulateAndMapWallEvents('streemshot'));
        this.streemshotDetections = this.config.roomWall.itemsByType.pipe(findInGroup('streemshotDetections'), accumulateAndMapWallEvents('streemshotDetections'), map(streemshots => streemshots
            .flatMap(detections => {
            const streemshotSid = detections.streemshotId ?? '';
            const streemshotDetections = detections.detections?.map(det => ({ ...det, streemshotSid })) ?? [];
            return streemshotDetections;
        })
            .sort((a, b) => {
            if (a.type > b.type) {
                return -1;
            }
            else {
                return 1;
            }
        })), shareReplay(1));
    }
    getDetections({ types, streemshotId, }) {
        let finalRx = combineLatest([this.streemshotDetections, this.streemshots]).pipe(map(([detections, streemshots]) => {
            return detections
                .map((detection) => {
                const matchingStreemshot = streemshots.find((s) => s.id === detection.streemshotSid);
                invariant(matchingStreemshot, 'Could not find matching streemshot for detection');
                return {
                    ...detection,
                    streemshotCreatedAt: matchingStreemshot.clientCreatedAt,
                };
            })
                .sort((a, b) => (a.streemshotCreatedAt?.seconds?.toNumber() ?? 0) -
                (b.streemshotCreatedAt?.seconds?.toNumber() ?? 0));
        }));
        if (types) {
            finalRx = finalRx.pipe(map(wallItems => wallItems.filter(item => types.includes(item.type))));
        }
        if (streemshotId) {
            finalRx = finalRx.pipe(filterByProperty('streemshotSid', streemshotId.toString()));
        }
        return finalRx;
    }
    // Creating a detection adds a userDetectionFeedback item to the wall. Streem service will see updates to this item and generate/update corresponding streemshotDetections as needed.
    createDetection(streemshotId, description, type) {
        logger.debug(`Creating new detection for streemshot ${streemshotId}`);
        this.config.roomWall.createDetection(streemshotId, description, type);
    }
    async updateDetection(detectionId, newDescription, newType) {
        const detections = await takeFirstEmitted(this.streemshotDetections);
        const detection = detections.find(x => x.detectionId === detectionId.toString());
        invariant(detection, 'Could not find a detection to update');
        const streemshotId = new StreemshotId(detection.streemshotSid);
        // Type can’t truly be changed/switched since the type is core to the detection
        // Only way to change type is to hide old detection & recreate new one
        logger.debug(`Updating detection ${detectionId}`);
        if (detection.type !== newType) {
            this.config.roomWall.hideDetection(streemshotId, detectionId);
            this.config.roomWall.createDetection(streemshotId, newDescription, newType);
        }
        else {
            this.config.roomWall.updateDetection(streemshotId, detectionId, newDescription);
        }
    }
    async deleteDetection(detectionId) {
        logger.debug(`Hiding detection ${detectionId}`);
        const detections = await takeFirstEmitted(this.streemshotDetections);
        const detection = detections.find(x => x.detectionId === detectionId.toString());
        invariant(detection, 'Could not find a detection to delete');
        const streemshotId = new StreemshotId(detection.streemshotSid);
        this.config.roomWall.hideDetection(streemshotId, detectionId);
    }
}
//# sourceMappingURL=detection_controller.js.map